import { PropType, reactive, ref } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { cloneDeep, isEqual, sumBy } from 'lodash-es';
import { useAside, useNotif } from '@orion.ui/orion';
import ApexCharts, { ApexOptions } from 'apexcharts';
import DashboardSignatureOrderAside from '@/components/dashboard/DashboardSignatureOrderAside.vue';
import useSignatureBalanceService from '@/services/SignatureBalanceService';

type Props = SetupProps<typeof DashboardSignatureBalanceRepartitionAsideSetupService.props>
type ApexFormatterOptions = {
	seriesIndex: number
	dataPointIndex: number,
	w: {globals: any}
}

export default class DashboardSignatureBalanceRepartitionAsideSetupService extends BaseSetupService<Props> {
	static readonly props = {
		balancies: {
			type: Array as PropType<ICurrentSignatureBalanceModel[]>,
			default: () => [],
		},
	};

	readonly _interlocutor = ref<OrionInput[]>();
	readonly _signaturesChart = ref<HTMLElement>();
	readonly _aside?: OrionAside;

	private readonly state = reactive({
		newBalancies: [] as ICurrentSignatureBalanceModel[],
		balanciesFromProps: [] as ICurrentSignatureBalanceModel[],
		isLoading: false,
		chart: undefined as Undef<ApexCharts>,
	});

	get isLoading () { return this.state.isLoading; }
	get newBalancies () { return this.state.newBalancies; }

	get totalSignatures () {
		return sumBy(this.state.newBalancies, x => x.balance);
	}

	get hasChanges () {
		return !isEqual(this.state.balanciesFromProps, this.state.newBalancies);
	}


	constructor (props: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}

	protected onMounted () {
		this.state.balanciesFromProps = cloneDeep(this.props.balancies);
		this.state.newBalancies = cloneDeep(this.props.balancies);

		if (this.state.balanciesFromProps.length > 1) {
			this.initSignaturesChart();
		}
	}

	protected onBeforeUnmount (): void {
		this.state.chart?.destroy();
	}


	cancel () {
		this.state.newBalancies = cloneDeep(this.state.balanciesFromProps);
	}

	async saveAsync () {
		this.state.isLoading = true;
		let isValid = true;

		if (!this._interlocutor.value) return;
		for (const ref of this._interlocutor.value) {
			if (!ref.isValid()) {
				isValid = false;
				break;
			}
		}

		if (!isValid) {
			this.state.isLoading = false;
			return useNotif.warning(`L'une des adresses mail n'est pas valide`);
		};

		try {
			this.state.newBalancies.forEach(async (newBalance) => {
				const balance = this.props.balancies.find(balance => balance.id === newBalance.id);

				if (balance && balance.interlocutor !== newBalance.interlocutor) {
					await useSignatureBalanceService().updateInterlocutorAsync(newBalance);
				}
				useNotif.success(`Modifications enregistrées.`);
				this._aside?.trigger('save', this.newBalancies);
				this.state.balanciesFromProps = cloneDeep(this.newBalancies);
			});
		} catch (error: any) {
			useNotif.danger(error?.response?.data);
		} finally {
			this.state.isLoading = false;
		}
	}


	stickerTitle (balance: ICurrentSignatureBalanceModel) {
		return `${'signature restante'.pluralize(balance.balance)}`;
	}

	stickerThreshold (balance: ICurrentSignatureBalanceModel) {
		return `${'signature'.pluralize(balance.notificationThreshold)}`;
	}

	balanceIsLow (balance: ICurrentSignatureBalanceModel) {
		return balance.balance < balance.notificationThreshold;
	}

	orderPack (balance: ICurrentSignatureBalanceModel) {
		useAside({
			Nested: DashboardSignatureOrderAside,
			NestedProps: { balance },
		});
	}

	initSignaturesChart () {
		if (!this._signaturesChart.value) return;

		const series = this.state.newBalancies.map(x => x.balance);
		const labels = this.state.newBalancies.map(x => x.companyNames.join(', '));

		const options: ApexOptions = {
			series,
			labels,
			chart: {
				type: 'donut',
				height: 300,
				toolbar: { show: false },
			},
			legend: {
				show: true,
				position: 'bottom',
				formatter: (seriesName: string, opts: ApexFormatterOptions) => {
					const legend = seriesName.length > 15
						? `${seriesName.slice(0, 15)}... : ${opts.w.globals.series[opts.seriesIndex]}`
						: `${seriesName}: ${opts.w.globals.series[opts.seriesIndex]}`;
					return legend;
				},
			},
			tooltip: {
				y: {
					formatter: (val: number) => 'signature restante'.pluralize(val),
					title: {
						formatter: (seriesName: string) => {
							const legend = seriesName.length > 15
								? `${seriesName.slice(0, 15)}... :`
								: `${seriesName}:`;
							return legend;
						},
					},
				},
			},
			plotOptions: { pie: { donut: { size: '60%' } } },
		};

		this.state.chart = new ApexCharts(this._signaturesChart.value, options);
		this.state.chart.render();
	}
}
